import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import { ResourceVersionListItem, VersionedResourceType } from '@/types';
import { dayjs } from '@/utilities';

type HistoryReference = ReturnType<typeof dayjs>;

export const HISTORY_MODAL_HEADER_HEIGHT = '4.5rem';

const RESOURCE_LABELS: Record<VersionedResourceType, string> = {
    dashboards: 'Dashboard',
    folders: 'Folder',
    importedEntities: 'Imported Entity',
    investments: 'Investment',
    listViews: 'List View',
    lists: 'List',
    memos: 'Memo',
    pipelines: 'Pipeline',
    reportComponents: 'Report Component',
    reports: 'Report',
    tasks: 'Task',
    teams: 'Team',
};

const RESOURCE_PLURAL_LABELS: Record<VersionedResourceType, string> = {
    dashboards: 'Dashboards',
    folders: 'Folders',
    importedEntities: 'Imported Entities',
    investments: 'Investments',
    listViews: 'List Views',
    lists: 'Lists',
    memos: 'Memos',
    pipelines: 'Pipelines',
    reportComponents: 'Report Components',
    reports: 'Reports',
    tasks: 'Tasks',
    teams: 'Teams',
};

export interface ResourceHistoryGroup {
    items: ResourceVersionListItem[];
    key: string;
    label: string;
}

export const HISTORY_TIME_RANGE_OPTIONS = [
    { label: 'Any time', value: 'all' },
    { label: 'Past 7 days', value: '7-days' },
    { label: 'Past 30 days', value: '30-days' },
    { label: 'Past 90 days', value: '90-days' },
] as const;

export type HistoryTimeRange = (typeof HISTORY_TIME_RANGE_OPTIONS)[number]['value'];

export interface ResourceHistoryFilters {
    ownerId: string;
    query: string;
    timeRange: HistoryTimeRange;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

export const getResourceHistoryLabel = (resourceType: VersionedResourceType): string => {
    if (resourceType === 'listViews' || resourceType === 'reportComponents') {
        return getWorkspaceResourceTypeLabel(resourceType);
    }

    return RESOURCE_LABELS[resourceType];
};

export const getResourceHistoryPluralLabel = (resourceType: VersionedResourceType): string => {
    if (resourceType === 'listViews' || resourceType === 'reportComponents') {
        return getWorkspaceResourceTypeLabel(resourceType, { plural: true });
    }

    return RESOURCE_PLURAL_LABELS[resourceType];
};

export const getHistoryPeriodLabel = (createdAt: string, reference: HistoryReference = dayjs()): string => {
    const created = dayjs(createdAt);

    if (!created.isValid()) {
        return 'Older';
    }

    if (created.isSame(reference, 'day')) {
        return 'Today';
    }

    if (created.isSame(reference.subtract(1, 'day'), 'day')) {
        return 'Yesterday';
    }

    const daysAgo = Math.max(0, reference.startOf('day').diff(created.startOf('day'), 'day'));

    if (daysAgo < 7) {
        return 'Earlier this week';
    }

    if (daysAgo < 35) {
        const weeksAgo = Math.max(1, Math.round(daysAgo / 7));
        return `${weeksAgo} ${weeksAgo === 1 ? 'week' : 'weeks'} ago`;
    }

    const monthsAgo = Math.max(1, Math.round(reference.diff(created, 'month', true)));

    if (monthsAgo < 12) {
        return `${monthsAgo} ${monthsAgo === 1 ? 'month' : 'months'} ago`;
    }

    const yearsAgo = Math.max(1, Math.round(reference.diff(created, 'year', true)));
    return `${yearsAgo} ${yearsAgo === 1 ? 'year' : 'years'} ago`;
};

export const groupHistoryVersions = (
    items: ResourceVersionListItem[],
    reference: HistoryReference = dayjs(),
): ResourceHistoryGroup[] => {
    const groups = new Map<string, ResourceHistoryGroup>();

    items.forEach((version) => {
        const label = getHistoryPeriodLabel(version.createdAt, reference);
        const group = groups.get(label);

        if (group) {
            group.items.push(version);
            return;
        }

        groups.set(label, {
            items: [version],
            key: label.toLowerCase().replaceAll(' ', '-'),
            label,
        });
    });

    return Array.from(groups.values());
};

export const getHistoryActorKey = (version: ResourceVersionListItem): string => {
    return version.actor?.id ?? 'unknown-owner';
};

export const getHistoryActorName = (version: ResourceVersionListItem): string => {
    const name = [version.actor?.firstName, version.actor?.lastName].filter(Boolean).join(' ');
    return name || version.actor?.emailAddress || 'Workspace member';
};

export const filterHistoryVersions = (
    items: ResourceVersionListItem[],
    filters: ResourceHistoryFilters,
    reference: HistoryReference = dayjs(),
): ResourceVersionListItem[] => {
    const normalizedQuery = filters.query.trim().toLowerCase();
    const daysByTimeRange: Record<Exclude<HistoryTimeRange, 'all'>, number> = {
        '7-days': 7,
        '30-days': 30,
        '90-days': 90,
    };

    return items.filter((version) => {
        if (filters.ownerId !== 'all' && getHistoryActorKey(version) !== filters.ownerId) {
            return false;
        }

        if (filters.timeRange !== 'all') {
            const cutoff = reference.subtract(daysByTimeRange[filters.timeRange], 'day');

            if (dayjs(version.createdAt).isBefore(cutoff)) {
                return false;
            }
        }

        if (!normalizedQuery) {
            return true;
        }

        const searchableText = [
            getHistoryActorName(version),
            version.actor?.emailAddress,
            version.eventType,
            version.title,
            dayjs(version.createdAt).format('MMM D, YYYY h:mm A'),
            getHistoryPeriodLabel(version.createdAt, reference),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

        return searchableText.includes(normalizedQuery);
    });
};

export const formatHistoryFieldLabel = (value: string): string => {
    const leaf = value.split('.').at(-1) ?? value;
    return leaf
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (character) => character.toUpperCase());
};

export const formatHistoryValue = (value: unknown): string => {
    if (value === undefined) {
        return 'Not set';
    }

    if (value === null) {
        return 'None';
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (typeof value === 'string') {
        return value || 'Empty';
    }

    if (typeof value === 'number' || typeof value === 'bigint') {
        return String(value);
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return 'Unsupported value';
    }
};

export const extractDocumentText = (value: unknown): string => {
    if (typeof value === 'string') {
        try {
            return extractDocumentText(JSON.parse(value));
        } catch {
            return value;
        }
    }

    if (Array.isArray(value)) {
        return value.map(extractDocumentText).filter(Boolean).join('\n');
    }

    if (!isRecord(value)) {
        return '';
    }

    const ownText = typeof value.text === 'string' ? value.text : '';
    const childText = extractDocumentText(value.content);

    if (ownText && childText) {
        return `${ownText}\n${childText}`;
    }

    return ownText || childText;
};

export const isRecordSnapshot = (value: unknown): value is Record<string, unknown> => isRecord(value);
